/* ----------------------------- */
/*   Reducer for the auth
/* ----------------------------- */

import {
  TOKEN_CHECKING,
  TOKEN_VALID,
  TOKEN_INVALID,
  SET_TOKEN,
  CLEAR_TOKEN,
  SET_USER,
  CLEAR_USER,
  SET_SAML_AUTH_URL_FETCHING,
  SET_SAML_AUTH_URL,
  SET_AUTHENTICATING,
  SAML_AUTH_ERROR,
  SET_TOKEN_AND_USER,
  CLEAR_TOKEN_AND_USER,
  TOKEN_SAVED_SYNCED_STORAGE,
  TOKEN_CLEARED_SYNCED_STORAGE,
  DELETE_ME_EVENTUALLY_CHANGE_USER_ROLE,
  DELETE_ME_EVENTUALLY_CHANGE_REMEDY_ID,
} from 'AppSrc/core/auth/auth-action-strings';


const defaultUserState = {
  id: null,
  firstName: null,
  lastName: null,
  roles: {
    fixed: null,
    mobile: null,
  },
  remedyIds: {
    fixed: null,
    mobile: null,
  },
};

const authReducerDefaultState = {
  // --- TOKEN ---
  // token = raw encoded JWT token
  token: null,
  tokenExpiryDate: null,
  // to flag when token has been detected invalid
  isTokenValid: false,
  // to flag token validity is being checked
  isTokenChecking: false,
  // to check the change analyst flag
  isChangeAnalyst: false,

  // --- SAML AUTH ---
  // indicate we are fetching SAML auth url
  isSAMLAuthUrlFetching: false,
  // SAML third party url
  SAMLAuthUrl: null,
  // indicate SAML auth is in progress
  isAuthenticating: false,
  // flag an error with the SAML auth
  SAMLAuthError: null,

  // flag that token is saved in syncedStorage (to sync login across tabs)
  isTokenSavedInSyncedStorage: false,

  // --- USER DATA ---
  user: defaultUserState,
};


const authReducer = (state = authReducerDefaultState, action) => {

  switch (action.type) {

    /* --- SYNCED_STORAGE --- */

    /* - flag when the token is saved and cleared in syncedStorage - */
    case TOKEN_SAVED_SYNCED_STORAGE:

      return {
        ...state,
        isTokenSavedInSyncedStorage: true,
      };

    case TOKEN_CLEARED_SYNCED_STORAGE:

      return {
        ...state,
        isTokenSavedInSyncedStorage: false,
      };

    /* --- TOKEN --- */
    /* - Flag token validity is being checked (for a token in localStorage) - */
    case TOKEN_CHECKING:

      return {
        ...state,
        isTokenChecking: true,
      };

    /* - Flag the token has been checked valid (for a token in localStorage) - */
    case TOKEN_VALID:

      return {
        ...state,
        isTokenValid: true,
        isTokenChecking: false,
      };

    /* - Flag the token has been detected invalid but do not clear it until the refresh request has completed - */
    case TOKEN_INVALID:

      return {
        ...state,
        isTokenValid: false,
        isTokenChecking: false,
      };

    /* - Init or update the token and its expiry date - */
    case SET_TOKEN:

      return {
        ...state,
        token: action.payload.token,
        tokenExpiryDate: action.payload.tokenExpiryDate,
        isTokenChecking: false,
        isTokenValid: true,
      };

    /* - Clear the token and its expiry date after they have been found invalid - */
    case CLEAR_TOKEN:

      return {
        ...state,
        token: null,
        tokenExpiryDate: null,
        isTokenValid: false,
        isTokenChecking: false,
      };

    /* --- USER --- */
    case SET_USER:

      return {
        ...state,
        user: action.payload.user,
      };

    case CLEAR_USER:

      return {
        ...state,
        user: defaultUserState,
      };


    /* --- SAML AUTH --- */
    /* - Flag we are fetching SAML auth url - */
    case SET_SAML_AUTH_URL_FETCHING:

      return {
        ...state,
        isSAMLAuthUrlFetching: true,
      };

    /* - Init SAML third party url - */
    case SET_SAML_AUTH_URL:

      return {
        ...state,
        SAMLAuthUrl: action.payload.authUrl,
        isSAMLAuthUrlFetching: false,
      };

    /* - Flag SAML auth proper is in progress - */
    case SET_AUTHENTICATING:

      return {
        ...state,
        isAuthenticating: true,
      };

    /* - Flag SAML auth failed - */
    case SAML_AUTH_ERROR:

      return {
        ...state,
        SAMLAuthUrl: null,
        isAuthenticating: false,
        isSAMLAuthUrlFetching: false,
        SAMLAuthError: action.payload.error,
      };

    /* - Init or update the token, token expiry date and user data at once - */
    case SET_TOKEN_AND_USER:


      // this is a good eslint rule to have but an even better solution would to not use
      // cases... We need to refactor this
      /* eslint-disable-next-line no-case-declarations */
      const oldUserId = state.user.id;
      // this is a good eslint rule to have but an even better solution would to not use
      // cases... We need to refactor this
      /* eslint-disable-next-line no-case-declarations */
      const newUserId = action.payload.user.id;

      return {
        ...state,
        token: action.payload.token,
        tokenExpiryDate: action.payload.tokenExpiryDate,
        // we only update user if the user id has changed
        user: oldUserId !== newUserId ? action.payload.user : state.user,
        isChangeAnalyst: action.payload.changeAnalyst,
        isAuthenticating: false,
        isTokenChecking: false,
        isTokenValid: true,
      };

    /* - Clear the token, token expiry date and user data at once - */
    /* - also clear SAML auth url that is time limited and need to be fetched anew for login - */
    case CLEAR_TOKEN_AND_USER:

      return {
        ...state,
        token: null,
        tokenExpiryDate: null,
        user: defaultUserState,
        isTokenValid: false,
        isTokenChecking: false,
        isChangeAnalyst: null,
        isAuthenticating: false,
        SAMLAuthUrl: null,
        SAMLAuthError: null,
      };

    case DELETE_ME_EVENTUALLY_CHANGE_USER_ROLE: {
      const { network, newRole } = action.payload;
      // also set a fake remedy id for the correct network
      const fakeRemedyId = 'fake-remedy-id';
      return {
        ...state,
        user: {
          ...state.user,
          roles: {
            ...state.user.roles,
            [network]: newRole,
          },
          remedyIds: {
            ...state.user.remedyIds,
            [network]: state.user.remedyIds[network] ? state.user.remedyIds[network] : fakeRemedyId,
          },
        },
      };
    }

    case DELETE_ME_EVENTUALLY_CHANGE_REMEDY_ID: {
      const { network, remedyId } = action.payload;
      return {
        ...state,
        user: {
          ...state.user,
          remedyIds: {
            ...state.user.remedyIds,
            [network]: remedyId,
          },
        },
      };
    }

    default:
      return state;
  }
};

export {
  authReducer,
  authReducerDefaultState,
};
